import {
  FormControl,
  FormControlLabel,
  Grid,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { LastStepVM, ParentVM, SectionVM } from 'civil-status/view-models/step-form';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import { DatePicker, TimePicker } from '@mui/lab';
import ListNumber from '../list/list-number';
import ListOpposition from '../list/list-opposition';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import Foreign from '../../shared/foreign';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import Italian from '../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import PmiTextField from 'shell/components/shared/text-field';
import ListPublication from '../list/list-publication';
import { Checkbox } from '@mui/material';
import Place from './place';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import ListWifeHusband from '../list/list-wife-husband';
import { ID_OTHER_RITUAL_RELIGIOUS } from 'civil-status/constants/id-ritual-religious';
import AutocompleteListAtti from 'civil-status/components/shared/autocomplete-list-atti';
import { initialValuesParent } from 'civil-status/constants/initial-values-step';
import { Radio } from '@mui/material';

interface SpecificDataCivilUnionMarriageProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  controllOpposition: (opposition?: string) => void;
  controllGenericImpediment: (genericImpediment?: string) => void;
  controllImpedimentUnitedCivilly: (impedimentUnitedCivilly?: string) => void;
  controllPublication: (publication?: string) => void;
  controllMinorGroomAssistance: (minorGroomAssistance?: string) => void;
  controllMinorWifeAssistance: (minorWifeAssistance?: string) => void;
  controllAgeDefect: (ageDefect?: string, propertyRegime?: string) => void;
  controllTemporaryBanRemarriage: (isTemporaryBanRemarriage?: boolean) => void;
  controllRelationship: (relationship?: string) => void;
  controllNumberWitnesses: (numberWitnesses?: string) => void;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllIsSonUnderFourteen: (isSonUnderFourteen?: boolean) => void;
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence: (
    typologyConsentAndConfiguration?: ItemVM,
    typeDeclaration?: string,
    parentRecognition?: string,
    isIncestuousSonSentence?: boolean
  ) => void;
  configuration?: string;
  keySection: string;
}

const SpecificDataCivilUnionMarriageComponent = ({
  formContext,
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  controllOpposition,
  controllGenericImpediment,
  controllImpedimentUnitedCivilly,
  controllPublication,
  controllMinorGroomAssistance,
  controllMinorWifeAssistance,
  controllAgeDefect,
  controllTemporaryBanRemarriage,
  controllRelationship,
  controllNumberWitnesses,
  setDataInterprete,
  controllIsSonUnderFourteen,
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence,
  configuration,
  keySection,
}: SpecificDataCivilUnionMarriageProps): React.ReactElement => {
  const propertyRegime = useMemo(() => {
    return (formContext.values[KeySections.keyRegimePatrimoniale] as SectionVM)?.propertyRegime;
  }, [(formContext.values[KeySections.keyRegimePatrimoniale] as SectionVM)?.propertyRegime]);

  const isUcdtsp: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP;
  }, [configuration]);

  const isUcdtsp02: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP02;
  }, [configuration]);

  const isUcdtsp03: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP03;
  }, [configuration]);

  const isUcdtsp04: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP04;
  }, [configuration]);

  const isUcdtsp05: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP05;
  }, [configuration]);

  const isMadtsp: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP;
  }, [configuration]);

  const isMadtsp2: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP2;
  }, [configuration]);

  const isMadtsp3: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP3;
  }, [configuration]);

  const isMadtsp4: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP4;
  }, [configuration]);

  const isMadtsp5: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP5;
  }, [configuration]);

  const isMadtsp6: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP6;
  }, [configuration]);

  const isMadtsp7: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP7;
  }, [configuration]);

  const isMadtsp8: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP8;
  }, [configuration]);

  const isMadtsp9: boolean = useMemo(() => {
    return configuration === ConfigurationType.MADTSP9;
  }, [configuration]);

  const handleListAgeDefect = useEventCallback((item?: ItemVM) => {
    onChanges({
      ...values,
      ageDefect: item?.id?.toString(),
      minorGroomAssistance: '',
      minorWifeAssistance: '',
    } as SectionVM);
  });

  const handleListRelationship = useEventCallback((item?: ItemVM) => {
    onChange('relationship', item?.id);
  });

  const handleListMinorGroomAssistance = useEventCallback((item?: ItemVM) => {
    onChange('minorGroomAssistance', item?.id);
  });

  const handleListMinorWifeAssistance = useEventCallback((item?: ItemVM) => {
    onChange('minorWifeAssistance', item?.id);
  });

  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id);
  });

  const handleListPresentationDocumentRecognition = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocumentRecognition', item?.id);
  });

  const handleListNumberWitnesses = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('numberWitnesses', event.target.value);
  });

  const handleListOpposition = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('opposition', event.target.value);
  });

  const handleListGenericImpediment = useEventCallback((item?: ItemVM) => {
    onChange('genericImpediment', item?.id?.toString());
  });

  const handleListImpedimentUnitedCivilly = useEventCallback((item?: ItemVM) => {
    onChange('impedimentUnitedCivilly', item?.id?.toString());
  });

  const handleListWifeHusband = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('impedimentUnitedCivilly', event.target.value);
  });

  const handleListPublication = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('publication', event.target.value);
  });

  const handleListTypologyConsent = useEventCallback((item?: ItemVM) => {
    onChange('typologyConsent', item?.id);
  });

  const handleListDeclarant = useEventCallback((item?: ItemVM) => {
    onChange('declarant', item?.id);
  });

  const handleListRitualReligious = useEventCallback((item?: ItemVM) => {
    const valuesOfficianteMatr = formContext.values[KeySubjects.keyOfficianteMatr] as ParentVM;

    if (
      Boolean(valuesOfficianteMatr) &&
      (valuesOfficianteMatr as ParentVM)?.configuration === ConfigurationType.OFFI_ACATT
    ) {
      formContext.setValues({
        ...formContext.values,
        [KeySubjects.keyOfficianteMatr]: {
          ...initialValuesParent,
          configuration: valuesOfficianteMatr.configuration,
          flagComparente: valuesOfficianteMatr?.flagComparente,
          flagDichiarante: valuesOfficianteMatr?.flagDichiarante,
          tipoSoggetto: valuesOfficianteMatr?.tipoSoggetto,
          flagFirmatario: valuesOfficianteMatr?.flagFirmatario,
          presenza: valuesOfficianteMatr?.presenza,
          codeSubject: valuesOfficianteMatr.codeSubject,
          generality: {
            ...initialValuesParent.generality,
            idSubject: valuesOfficianteMatr.generality?.idSubject,
            isRequiredFromOtherAccordion:
              valuesOfficianteMatr?.generality?.isRequiredFromOtherAccordion || false,
          },
        } as ParentVM,
        [keySection]: {
          ...values,
          ritualReligious: item,
          otherRitualReligious: '',
        },
      });
      return;
    }
  });

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChanges({
        ...(values || {}),
        foreignDelegating: {
          ...(values || {}).foreignDelegating,
          [key]: value,
        },
      });
    },
    [values, onChanges]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChanges({
        ...(values || {}),
        foreignDelegating: {
          ...(valuesNew || {}),
        },
      });
    },
    [values, onChange]
  );

  const onChangeItalianDelegating = useCallback(
    (key, value) => {
      onChange('italianDelegating', {
        ...(values || {}).italianDelegating,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalianDelegating = useCallback(
    (valuesNew) => {
      onChange('italianDelegating', {
        ...(values || {}).italianDelegating,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const isDisabledMinorWifeAssistance = useMemo(() => {
    return !(propertyRegime === '2' && (values?.ageDefect === '3' || values?.ageDefect === '4'));
  }, [values?.ageDefect]);

  const isDisabledMinorGroomAssistance = useMemo(() => {
    return !(propertyRegime === '2' && (values?.ageDefect === '2' || values?.ageDefect === '4'));
  }, [values?.ageDefect]);

  useEffect(() => {
    controllOpposition(values?.opposition);
  }, [values?.opposition]);

  useEffect(() => {
    controllGenericImpediment(values?.genericImpediment);
  }, [values?.genericImpediment]);

  useEffect(() => {
    controllImpedimentUnitedCivilly(values?.impedimentUnitedCivilly);
  }, [values?.impedimentUnitedCivilly]);

  useEffect(() => {
    controllPublication(values?.publication);
  }, [values?.publication]);

  useEffect(() => {
    controllMinorGroomAssistance(values?.minorGroomAssistance);
  }, [values?.minorGroomAssistance]);

  useEffect(() => {
    controllMinorWifeAssistance(values?.minorWifeAssistance);
  }, [values?.minorWifeAssistance]);

  useEffect(() => {
    controllAgeDefect(values?.ageDefect, propertyRegime);
  }, [values?.ageDefect]);

  useEffect(() => {
    controllTemporaryBanRemarriage(values?.isTemporaryBanRemarriage);
  }, [values?.isTemporaryBanRemarriage]);

  useEffect(() => {
    controllRelationship(values?.relationship);
  }, [values?.relationship]);

  useEffect(() => {
    controllNumberWitnesses(values?.numberWitnesses);
  }, [values?.numberWitnesses]);

  useEffect(() => {
    setDataInterprete(values?.isWithInterprete);
  }, [values?.isWithInterprete]);

  useEffect(() => {
    controllIsSonUnderFourteen(values?.isSonUnderFourteen);
  }, [values?.isSonUnderFourteen]);

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence({
      id: configuration || '',
      value: values?.typologyConsent || '',
    });
  }, [values?.typologyConsent]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data evento *"
          aria-label="Data evento"
          value={values?.dateEvent || null}
          onChange={(date) => {
            onChange('dateEvent', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateEvent"
              fullWidth
              helperText={errors?.dateEvent}
              error={touched?.dateEvent && Boolean(errors?.dateEvent)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TimePicker
          label="Ora evento *"
          value={values?.timeEvent || null}
          disabled={disabled}
          onChange={(value) => onChange('timeEvent', value)}
          renderInput={(params) => (
            <PmiTextField
              id="timeEvent"
              name="timeEvent"
              {...params}
              fullWidth
              error={touched?.timeEvent && Boolean(errors?.timeEvent)}
              helperText={errors?.timeEvent}
            />
          )}
        />
      </Grid>
      {isUcdtsp03 ||
      isUcdtsp04 ||
      isUcdtsp05 ||
      isMadtsp3 ||
      isMadtsp4 ||
      isMadtsp6 ||
      isMadtsp7 ||
      isMadtsp9 ? (
        <Place
          values={values}
          onChange={onChange}
          onChanges={onChanges}
          disabled={disabled}
          errors={errors}
          touched={touched}
          isOnlyItalian={isUcdtsp03 || isMadtsp3 || isMadtsp7}
          isWithoutAddress={isMadtsp6 || isMadtsp9}
        />
      ) : undefined}
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Presentazione documenti"
          idDominio="36"
          value={values?.presentationDocument || ''}
          onChange={handleListPresentationDocument}
          disabled={disabled}
          error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
          required
        />
      </Grid>
      {isMadtsp6 || isMadtsp9 ? undefined : (
        <Grid item xs={12} md={6}>
          <ListNumber
            label="Numero dei testimoni"
            value={values?.numberWitnesses || ''}
            onChange={handleListNumberWitnesses}
            disabled={disabled}
            error={Boolean(errors?.numberWitnesses) && touched?.numberWitnesses}
            required
            isNumber2
          />
        </Grid>
      )}
      {isUcdtsp05 || isMadtsp7 ? undefined : (
        <Grid item xs={12} md={6}>
          <ListOpposition
            value={values?.opposition || ''}
            onChange={handleListOpposition}
            disabled={disabled}
            error={Boolean(errors?.opposition) && touched?.opposition}
          />
        </Grid>
      )}
      {isUcdtsp || isUcdtsp02 || isUcdtsp03 || isUcdtsp04 || isUcdtsp05 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Soggetto con impedimento a costituire l'unione civile"
            idDominio="95"
            value={values?.genericImpediment || ''}
            onChange={handleListGenericImpediment}
            disabled={disabled}
            error={Boolean(errors?.genericImpediment) && touched?.genericImpediment}
            required
          />
        </Grid>
      ) : undefined}
      {isMadtsp3 || isMadtsp4 ? (
        <Grid item xs={12} md={6}>
          <ListWifeHusband
            label="Soggetto con impedimento a recarsi nella casa comunale"
            value={values?.impedimentUnitedCivilly || ''}
            onChange={handleListWifeHusband}
            disabled={disabled}
            error={Boolean(errors?.impedimentUnitedCivilly) && touched?.impedimentUnitedCivilly}
            required
          />
        </Grid>
      ) : undefined}
      {isUcdtsp03 || isUcdtsp04 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Soggetto con impedimento a recarsi nella casa comunale"
            idDominio="95"
            value={values?.impedimentUnitedCivilly || ''}
            onChange={handleListImpedimentUnitedCivilly}
            disabled={disabled}
            error={Boolean(errors?.impedimentUnitedCivilly) && touched?.impedimentUnitedCivilly}
            required
            betweenFrom={1}
            betweenTo={3}
          />
        </Grid>
      ) : undefined}
      {isUcdtsp02 || isUcdtsp04 || isMadtsp2 || isMadtsp4 ? (
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data delega *"
            aria-label="Data delega"
            value={values?.dataDelegate || null}
            onChange={(date) => {
              onChange('dataDelegate', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dataDelegate"
                fullWidth
                helperText={errors?.dataDelegate}
                error={touched?.dataDelegate && Boolean(errors?.dataDelegate)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isUcdtsp02 || isUcdtsp04 || isMadtsp2 || isMadtsp4 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Nome USC delegante *"
              aria-label="Nome USC delegante"
              id="ufcDelegate"
              name="ufcDelegate"
              fullWidth
              disabled={disabled}
              helperText={errors?.ufcDelegate}
              error={touched?.ufcDelegate && Boolean(errors?.ufcDelegate)}
              type="text"
              variant="outlined"
              value={values?.ufcDelegate || ''}
              onChange={(event) => {
                onChange('ufcDelegate', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Motivo delega *"
              aria-label="Motivo delega"
              id="reason"
              name="reason"
              fullWidth
              disabled={disabled}
              helperText={errors?.reason}
              error={touched?.reason && Boolean(errors?.reason)}
              type="text"
              variant="outlined"
              value={values?.reason || ''}
              onChange={(event) => {
                onChange('reason', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isUcdtsp02 || isUcdtsp04 || isMadtsp2 || isMadtsp4 ? (
        <Fragment>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Stato"
                id="isForeignDelegating"
                value={values?.isForeignDelegating || 'false'}
                onChange={() => {
                  onChanges({
                    ...(values || {}),
                    isForeignDelegating: !values?.isForeignDelegating,
                    italianDelegating: {
                      location: '',
                      province: '',
                      municipality: null,
                    },
                    foreignDelegating: {
                      state: null,
                      province: '',
                      municipality: null,
                      consulate: undefined,
                    },
                  });
                }}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Comune delegante"
                  disabled={disabled}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Consolato delegante"
                  disabled={disabled}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {values?.isForeignDelegating ? (
            <Foreign
              disabledComponent={disabled}
              values={values?.foreignDelegating}
              onChange={onFieldForeignChange}
              onChanges={onFieldsForeignChange}
              errors={errors?.foreignDelegating as FormikErrors<ForeignForm>}
              touched={touched?.foreignDelegating as unknown as FormikTouched<ForeignForm>}
              labelState="Stato delegante"
              required
              isWithConsulate
            />
          ) : (
            <Italian
              disabledComponent={disabled}
              values={values?.italianDelegating}
              onChange={onChangeItalianDelegating}
              onChanges={onChangesItalianDelegating}
              errors={errors?.italianDelegating as FormikErrors<ItalianForm>}
              touched={touched?.italianDelegating as unknown as FormikTouched<ItalianForm>}
              labelMunicipality="Comune delegante"
              labelProvince="Provincia delegante"
              required
              fromDate={values?.dataDelegate || null}
            />
          )}
        </Fragment>
      ) : undefined}
      {isMadtsp5 ? (
        <Fragment>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isSonUnderFourteen"
                  checked={values?.isSonUnderFourteen || false}
                  disabled={disabled}
                  onChange={() => {
                    onChange('isSonUnderFourteen', !values?.isSonUnderFourteen);
                  }}
                  inputProps={{ 'aria-label': 'Figlio infraquattordicenne' }}
                />
              }
              label="Figlio infraquattordicenne"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Tipologia consenso"
              idDominio="12"
              value={values?.typologyConsent || ''}
              onChange={handleListTypologyConsent}
              disabled={disabled}
              error={Boolean(errors?.typologyConsent) && touched?.typologyConsent}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Presentazione documenti riconoscimento"
              idDominio="36"
              value={values?.presentationDocumentRecognition || ''}
              onChange={handleListPresentationDocumentRecognition}
              disabled={disabled}
              error={
                Boolean(errors?.presentationDocumentRecognition) &&
                touched?.presentationDocumentRecognition
              }
              required
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isMadtsp || isMadtsp2 || isMadtsp3 || isMadtsp4 || isMadtsp5 || isMadtsp6 || isMadtsp9 ? (
        <Grid item xs={12} md={6}>
          <ListPublication
            value={values?.publication || ''}
            onChange={handleListPublication}
            disabled={disabled}
            error={Boolean(errors?.publication) && touched?.publication}
            required
          />
        </Grid>
      ) : undefined}
      {isMadtsp ||
      isMadtsp2 ||
      isMadtsp3 ||
      isMadtsp4 ||
      isMadtsp5 ||
      isMadtsp6 ||
      isMadtsp7 ||
      isMadtsp8 ||
      isMadtsp9 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Difetto di età"
              idDominio="47"
              value={values?.ageDefect || ''}
              onChange={handleListAgeDefect}
              disabled={disabled}
              error={Boolean(errors?.ageDefect) && touched?.ageDefect}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Impedimento per parentela"
              idDominio="48"
              value={values?.relationship || ''}
              onChange={handleListRelationship}
              disabled={disabled}
              error={Boolean(errors?.relationship) && touched?.relationship}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  id="flagInternal"
                  checked={values?.isTemporaryBanRemarriage || false}
                  disabled={disabled}
                  onChange={() => {
                    onChange('isTemporaryBanRemarriage', !values?.isTemporaryBanRemarriage);
                  }}
                  inputProps={{ 'aria-label': 'Divieto temporaneo di nuove nozze' }}
                />
              }
              label="Divieto temporaneo di nuove nozze"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Assistenza minore Sposo"
              idDominio="51"
              value={values?.minorGroomAssistance || ''}
              onChange={handleListMinorGroomAssistance}
              disabled={disabled || isDisabledMinorGroomAssistance}
              error={Boolean(errors?.minorGroomAssistance) && touched?.minorGroomAssistance}
              required={!isDisabledMinorGroomAssistance}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Assistenza minore Sposa"
              idDominio="51"
              value={values?.minorWifeAssistance || ''}
              onChange={handleListMinorWifeAssistance}
              disabled={disabled || isDisabledMinorWifeAssistance}
              error={Boolean(errors?.minorWifeAssistance) && touched?.minorWifeAssistance}
              required={!isDisabledMinorWifeAssistance}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isMadtsp6 || isMadtsp9 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Dichiarante"
            idDominio="52"
            value={values?.declarant || ''}
            onChange={handleListDeclarant}
            disabled={disabled}
            error={Boolean(errors?.declarant) && touched?.declarant}
            required
          />
        </Grid>
      ) : undefined}
      {isMadtsp6 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <AutocompleteListAtti
              label="Rito religioso"
              idDominio="8"
              onChange={handleListRitualReligious}
              value={values?.ritualReligious?.id?.toString() || ''}
              error={Boolean(errors?.ritualReligious) && touched?.ritualReligious}
              required
            />
          </Grid>
          {values?.ritualReligious?.id?.toString() === ID_OTHER_RITUAL_RELIGIOUS ? (
            <Grid item xs={12}>
              <PmiTextField
                label="Specificare altro rito *"
                aria-label="Specificare altro rito"
                id="otherRitualReligious"
                name="otherRitualReligious"
                fullWidth
                variant="outlined"
                value={values?.otherRitualReligious || ''}
                onChange={(event) => {
                  onChange('otherRitualReligious', event.target.value);
                }}
                disabled={disabled}
                error={touched?.otherRitualReligious && Boolean(errors?.otherRitualReligious)}
                helperText={errors?.otherRitualReligious}
              />
            </Grid>
          ) : undefined}
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isRecognizedRite"
                  checked={values?.isRecognizedRite || false}
                  onChange={() => {
                    onChange('isRecognizedRite', !values?.isRecognizedRite);
                  }}
                  inputProps={{ 'aria-label': 'Rito riconosciuto' }}
                  disabled={disabled}
                />
              }
              label="Rito riconosciuto"
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {!(isMadtsp6 || isMadtsp9) ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isWithInterprete"
                checked={values?.isWithInterprete || false}
                onChange={() => {
                  onChange('isWithInterprete', !values?.isWithInterprete);
                }}
                inputProps={{ 'aria-label': 'Presenza interprete' }}
                disabled={disabled}
              />
            }
            label="Presenza interprete"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

SpecificDataCivilUnionMarriageComponent.displayName =
  'Form Specific Data Civil - Marriage Union component';

const SpecificDataCivilUnionMarriage = memo(SpecificDataCivilUnionMarriageComponent);

export default SpecificDataCivilUnionMarriage;
